import { Instant, LocalDate } from '@js-joda/core';

import { Disposable } from './interfaces';

// Every delay, interval and community date goes through this seam, so months of scheduling stay testable in virtual time.
export abstract class Clock {
  // A count that only ever grows is how a timer leak shows up in the heartbeat and in the soak test.
  public abstract readonly activeTimers: number;

  public abstract now(): Instant;
  public abstract after(milliseconds: number, signal?: AbortSignal): Promise<void>;
  public abstract every(milliseconds: number, jitterMilliseconds: number, callback: () => void): Disposable;

  public today(timezone: string): LocalDate {
    return this.communityDate(this.now(), timezone);
  }

  // The community's own calendar day, which is what every feed window and expiry is measured against.
  protected communityDate(instant: Instant, timezone: string): LocalDate {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(instant.toEpochMilli());
    const part = (type: string): number => Number(parts.find(item => item.type === type)?.value);

    return LocalDate.of(part('year'), part('month'), part('day'));
  }

  // A NaN or negative delay would fire a repeating timer in a tight loop rather than failing visibly.
  protected assertValidDelay(milliseconds: number, repeating: boolean = false): void {
    if (!Number.isFinite(milliseconds) || milliseconds < 0 || (repeating && milliseconds === 0)) {
      throw new RangeError('Invalid clock delay');
    }
  }
}
